import { useEffect, useRef, useState } from "react";
import { loadClarity, loadGoogleAnalytics } from "../../helpers/analytics";
import { Analytics } from '@vercel/analytics/react';


export default function ConsentManager() {
    const originalMethodsRef = useRef({});
    const isWrappedRef = useRef(false);
    const [hasAnalyticsConsent, setHasAnalyticsConsent] = useState(false);

    useEffect(() => {
        let pollInterval;
        let timeoutId;

        const runConsentCheck = () => {
            try {
                const consent = window.__enzuzoApi?.consent?.getConsent?.();
                console.log("Consent updated:", consent);

                if (consent?.analytics) {
                    // Enable Vercel Analytics
                    setHasAnalyticsConsent(true);
                    
                    // Load analytics scripts in parallel with error handling
                    Promise.allSettled([
                        loadGoogleAnalytics(),
                        loadClarity()
                    ]).then(results => {
                        results.forEach((result, idx) => {
                            if (result.status === 'rejected') {
                                const name = idx === 0 ? 'Google Analytics' : 'Clarity';
                                console.warn(`[ConsentManager] ${name} failed to load:`, result.reason);
                            }
                        });
                    });
                } else {
                    console.log("Analytics disabled by user.");
                    setHasAnalyticsConsent(false);
                }
            } catch (error) {
                console.warn('[ConsentManager] Error checking consent:', error);
                // If Enzuzo is broken, assume analytics consent is given for basic functionality
                // This is a fallback to ensure the app doesn't break
                console.log('[ConsentManager] Enzuzo error detected, loading analytics as fallback');
                setHasAnalyticsConsent(true);
            }
        };

        const setupMethodWrapping = () => {
            try {
                const control = window.__enzuzoApi?.control;
                if (!control || isWrappedRef.current) return;

                // Store original methods before wrapping
                ["acceptAll", "acceptPartial", "declineAll"].forEach(fnName => {
                    if (typeof control[fnName] === 'function' && !originalMethodsRef.current[fnName]) {
                        originalMethodsRef.current[fnName] = control[fnName];

                        // Wrap method to detect consent changes
                        control[fnName] = (...args) => {
                            try {
                                const result = originalMethodsRef.current[fnName].apply(control, args);
                                runConsentCheck();
                                return result;
                            } catch (error) {
                                console.warn(`[ConsentManager] Error in wrapped method ${fnName}:`, error);
                                // Fallback to original method if wrapping fails
                                return originalMethodsRef.current[fnName].apply(control, args);
                            }
                        };
                    }
                });

                isWrappedRef.current = true;
            } catch (error) {
                console.warn('[ConsentManager] Error setting up method wrapping:', error);
            }
        };

        const initEnzuzo = () => {
            try {
                if (!window.__enzuzoApi) return;

                // Enzuzo loaded successfully
                clearInterval(pollInterval);
                clearTimeout(timeoutId);

                // Run initial consent check
                runConsentCheck();

                // Set up method wrapping to detect consent changes
                setupMethodWrapping();
            } catch (error) {
                console.warn('[ConsentManager] Error initializing Enzuzo:', error);
                // If Enzuzo fails to initialize, load analytics as fallback
                runConsentCheck();
            }
        };

        // Add global error handler for Clarity script errors
        const handleClarityError = (event) => {
            if (event.error && event.error.message && event.error.message.includes('a[c]')) {
                console.warn('[ConsentManager] Detected Clarity script error, continuing with analytics');
                event.preventDefault(); // Prevent the error from breaking the app
                // Don't re-run consent check as it might cause infinite loop
            }
        };

        window.addEventListener('error', handleClarityError);

        // Poll for Enzuzo to load (every 500ms)
        pollInterval = setInterval(initEnzuzo, 500);

        // Timeout after 5 seconds
        timeoutId = setTimeout(() => {
            clearInterval(pollInterval);
            if (!window.__enzuzoApi) {
                console.warn('[ConsentManager] Enzuzo failed to load within 5 seconds - loading analytics as fallback');
                setHasAnalyticsConsent(true);
                runConsentCheck();
            }
        }, 5000);

        // Cleanup function
        return () => {
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            window.removeEventListener('error', handleClarityError);

            // Restore original Enzuzo methods
            try {
                const control = window.__enzuzoApi?.control;
                if (control) {
                    Object.keys(originalMethodsRef.current).forEach(fnName => {
                        if (originalMethodsRef.current[fnName]) {
                            control[fnName] = originalMethodsRef.current[fnName];
                        }
                    });
                }
            } catch (error) {
                console.warn('[ConsentManager] Error during cleanup:', error);
            }

            isWrappedRef.current = false;
        };
    }, []);

    return hasAnalyticsConsent ? <Analytics /> : null;
}
